import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { DeviceIdentityService } from '../services/device-identity.service';

/**
 * Attaches the X-Edge-Client-Device-Id header to every outbound API
 * call so the backend can resolve the caller's row in
 * `edge_client_devices` and stamp its Guid id onto every POS write +
 * audit row.
 *
 * Header value comes from {@link DeviceIdentityService}, which sources the
 * hardware id durably (Flutter `device:info` bridge → localStorage cache →
 * legacy `sessionStorage.edgeClientDeviceId`) and falls back to the literal
 * `"web"` sentinel for plain browser sessions. This is resilient to the
 * POS-boot storage clear and reloads, unlike the old transient-sessionStorage
 * read.
 *
 * Maps to the registered Edge Client device the operator picked at the
 * registration step. Distinct from any per-request idempotency keys —
 * this is a long-lived per-terminal identity header.
 */
const HEADER_NAME = 'X-Edge-Client-Device-Id';
const WEB_SENTINEL = 'web';

export const edgeClientDeviceInterceptor: HttpInterceptorFn = (req, next) => {
  let value = WEB_SENTINEL;
  try {
    value = inject(DeviceIdentityService).getEdgeClientId();
  } catch {
    // DI/storage access can throw in SSR / sandboxed iframes; treat as a
    // browser-only session and fall back to the sentinel.
  }

  const enriched = req.clone({ setHeaders: { [HEADER_NAME]: value } });
  return next(enriched);
};
